import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChatDataSourceProvider } from "./DataSourceContext";
import { GetHttpClient } from "../Utils/HttpClient";
import { readJsonResponse } from "../Utils/request";

const API_PATHS = {
  ROOM: "/room",
  MESSAGE: "/msg",
  FILE: "/file",
};

const ensureRoomId = (payload = {}) => {
  const rawId =
    payload?.RoomId ??
    payload?.roomId ??
    payload?.Room?.RoomId ??
    payload?.room?.RoomId ??
    null;
  const roomId = rawId !== null && rawId !== undefined ? Number(rawId) : null;
  if (!roomId && roomId !== 0) {
    throw new Error("RoomId is required.");
  }
  return roomId;
};

const createRoomsApi = (client) => ({
  fetch: (params = {}) => readJsonResponse(client.Get(API_PATHS.ROOM, params)),
  create: (body = {}) => readJsonResponse(client.Post(API_PATHS.ROOM, body)),
  update: ({ RoomId, body, ...rest }) => {
    const targetId = ensureRoomId({ RoomId, ...rest });
    const payload = body ?? rest?.body ?? rest;
    return readJsonResponse(
      client.Put(`${API_PATHS.ROOM}?RoomId=${targetId}`, payload)
    );
  },
  remove: ({ RoomId, ...rest }) => {
    const targetId = ensureRoomId({ RoomId, ...rest });
    return readJsonResponse(
      client.Request(`${API_PATHS.ROOM}?RoomId=${targetId}`, {
        method: "DELETE",
      })
    );
  },
  toggleFavorite: ({ RoomId, ...rest }) => {
    const targetId = ensureRoomId({ RoomId, ...rest });
    return readJsonResponse(
      client.Get(`${API_PATHS.ROOM}/fav`, { RoomId: targetId })
    );
  },
  getOrCreate: (body = {}) =>
    readJsonResponse(client.Patch(API_PATHS.ROOM, body)),
});

const createMessagesApi = (client) => ({
  fetch: (params = {}) =>
    readJsonResponse(client.Get(API_PATHS.MESSAGE, params)),
  remove: (body = {}) =>
    readJsonResponse(client.Delete(API_PATHS.MESSAGE, body, {})),
});

const createFileApi = (client) => ({
  uploadChunk: ({ UploadId, ChunkIndex, TempId, body }) => {
    if (!UploadId && UploadId !== 0) {
      throw new Error("UploadId is required for chunk upload.");
    }
    const query = new URLSearchParams({
      UploadId,
      ChunkIndex: ChunkIndex ?? 0,
      TempId: TempId || "",
    });
    return readJsonResponse(
      client.Request(`${API_PATHS.FILE}/upload-chunk?${query.toString()}`, {
        method: "POST",
        body,
      })
    );
  },
});

const createQueryHook = (fetcher) => {
  return function useChatQuery(params = {}, options = {}) {
    const { skip = false } = options;
    const [state, setState] = useState({
      data: null,
      isLoading: false,
      isFetching: false,
      isError: false,
      error: null,
      isUninitialized: true,
    });
    const mountedRef = useRef(true);
    const paramsRef = useRef(params);
    useEffect(() => {
      return () => {
        mountedRef.current = false;
      };
    }, []);
    const paramsKey = useMemo(() => JSON.stringify(params || {}), [params]);
    useEffect(() => {
      paramsRef.current = params;
    }, [paramsKey, params]);
    const executeFetch = useCallback(
      async (overrideParams, { silent = false } = {}) => {
        if (skip) {
          return { data: null };
        }
        setState((prev) => ({
          ...prev,
          isLoading: !silent && prev.isUninitialized ? true : prev.isLoading,
          isFetching: true,
          isError: false,
          error: null,
          isUninitialized: silent ? prev.isUninitialized : false,
        }));
        try {
          const result = await fetcher(
            overrideParams !== undefined ? overrideParams : paramsRef.current
          );
          if (mountedRef.current) {
            setState({
              data: result,
              isLoading: false,
              isFetching: false,
              isError: false,
              error: null,
              isUninitialized: false,
            });
          }
          return { data: result };
        } catch (error) {
          if (mountedRef.current) {
            setState((prev) => ({
              ...prev,
              isLoading: false,
              isFetching: false,
              isError: true,
              error,
              isUninitialized: false,
            }));
          }
          throw error;
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [fetcher, skip]
    );
    useEffect(() => {
      if (skip) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          isFetching: false,
          isUninitialized: prev.isUninitialized,
        }));
        return;
      }
      let cancelled = false;
      setState((prev) => ({
        ...prev,
        isLoading: prev.isUninitialized,
        isFetching: true,
        isError: false,
        error: null,
        isUninitialized: false,
      }));
      fetcher(paramsRef.current)
        .then((result) => {
          if (cancelled || !mountedRef.current) return;
          setState({
            data: result,
            isLoading: false,
            isFetching: false,
            isError: false,
            error: null,
            isUninitialized: false,
          });
        })
        .catch((error) => {
          if (cancelled || !mountedRef.current) return;
          setState((prev) => ({
            ...prev,
            isLoading: false,
            isFetching: false,
            isError: true,
            error,
            isUninitialized: false,
          }));
        });
      return () => {
        cancelled = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paramsKey, skip, fetcher]);
    const refetch = useCallback(
      () => executeFetch(paramsRef.current, { silent: true }),
      [executeFetch]
    );
    return {
      ...state,
      refetch,
    };
  };
};

const useMutationRunner = (handler) => {
  const [state, setState] = useState({
    isLoading: false,
    isError: false,
    error: null,
  });
  const run = useCallback(
    async (payload) => {
      setState({ isLoading: true, isError: false, error: null });
      try {
        const result = await handler(payload);
        setState({ isLoading: false, isError: false, error: null });
        return { data: result };
      } catch (error) {
        setState({ isLoading: false, isError: true, error });
        throw error;
      }
    },
    [handler]
  );
  return useMemo(
    () => ({
      run,
      state,
    }),
    [run, state]
  );
};

export default function ReduxDataSourceProvider({ children }) {
  const client = useMemo(() => GetHttpClient(), []);
  const roomsApi = useMemo(() => createRoomsApi(client), [client]);
  const messagesApi = useMemo(() => createMessagesApi(client), [client]);
  const fileApi = useMemo(() => createFileApi(client), [client]);

  const useRoomsQuery = useMemo(
    () => createQueryHook(roomsApi.fetch),
    [roomsApi]
  );
  const useMessagesQuery = useMemo(
    () => createQueryHook(messagesApi.fetch),
    [messagesApi]
  );

  const RoomsListMutation = useMutationRunner(roomsApi.fetch);
  const RoomsCreateMutation = useMutationRunner(roomsApi.create);
  const RoomsUpdateMutation = useMutationRunner(roomsApi.update);
  const RoomsDeleteMutation = useMutationRunner(roomsApi.remove);
  const RoomsFavoriteMutation = useMutationRunner(roomsApi.toggleFavorite);
  const RoomsGetOrCreateMutation = useMutationRunner(roomsApi.getOrCreate);

  const UploadChunkMutation = useMutationRunner(fileApi.uploadChunk);
  const DeleteMessageMutation = useMutationRunner(messagesApi.remove);

  const value = useMemo(
    () => ({
      Rooms: {
        UseRoomsQuery: useRoomsQuery,
        List: RoomsListMutation,
        Create: RoomsCreateMutation,
        Update: RoomsUpdateMutation,
        Delete: RoomsDeleteMutation,
        ToggleFavorite: RoomsFavoriteMutation,
        GetOrCreate: RoomsGetOrCreateMutation,
      },
      Messages: {
        UploadChunk: UploadChunkMutation,
        Delete: DeleteMessageMutation,
        UseMessagesQuery: useMessagesQuery,
      },
    }),
    [
      useRoomsQuery,
      useMessagesQuery,
      RoomsListMutation,
      RoomsCreateMutation,
      RoomsUpdateMutation,
      RoomsDeleteMutation,
      RoomsFavoriteMutation,
      RoomsGetOrCreateMutation,
      UploadChunkMutation,
      DeleteMessageMutation,
    ]
  );

  return (
    <ChatDataSourceProvider value={value}>{children}</ChatDataSourceProvider>
  );
}
